'use client';
import { useEffect, useState } from 'react';
import { usePathname } from 'next/navigation';
import { useDisplayDurationQuery } from '@/hooks/useCollectableCoupon';
import {
  CollectableCouponDisplayMode,
  ICollectableCouponPageSetting,
  ICollectableCouponSetting,
} from '@/interfaces/collectableCoupon';

export interface CollectableCouponTimerApi {
  /** Show the floating trigger when true; hide it when false. */
  showFloatingButton: boolean;
  /** Force-hide the trigger (e.g. when the last coupon was collected). */
  hideFloatingButton: () => void;
  /** Page-specific setting resolved against the current route (Homepage/ProductPage/OtherPages). */
  pageSetting: ICollectableCouponPageSetting | undefined;
  /** Full settings payload from the API (null when not configured yet). */
  settings: ICollectableCouponSetting | null | undefined;
}

// Frontend default — applied whenever the backend has not configured a value.
const DEFAULT_DURATION_SECONDS = 10;
const TIME_BASED: CollectableCouponDisplayMode = 'TimeBased';

const buildFallbackPageSetting = (
  durationSeconds: number,
): ICollectableCouponPageSetting => ({
  Mode: TIME_BASED,
  DurationSeconds: durationSeconds,
});

/**
 * Resolve which page setting applies to the current route.
 * Homepage = exact "/" or "/home"; ProductPage = "/product/..."; OtherPages = everything else.
 * When the API has not yet returned configured settings, fall back to TimeBased with the
 * default duration (DEFAULT_DURATION_SECONDS, currently 10s) so the FAB still behaves
 * consistently before the network reply.
 */
const resolvePageSetting = (
  pathName: string | null | undefined,
  settings: ICollectableCouponSetting | null | undefined,
): ICollectableCouponPageSetting | undefined => {
  const fallback = buildFallbackPageSetting(DEFAULT_DURATION_SECONDS);

  if (!settings) {
    return fallback;
  }

  const p = pathName ?? '';

  if (p === '/' || p === '/home') {
    return settings.Homepage ?? fallback;
  }

  if (p.startsWith('/product/')) {
    return settings.ProductPage ?? fallback;
  }

  return settings.OtherPages ?? fallback;
};

/**
 * Auto-hide the floating trigger based on the per-page admin setting.
 *  - AlwaysDisplay: never schedule a hide timeout; the FAB stays visible indefinitely.
 *  - TimeBased: hide the FAB after the page-specific DurationSeconds.
 *
 * The timer restarts on every route change.
 * The timer is paused while `availableCount` is 0 so we don't waste countdown on an empty list.
 */
export const useCollectableCouponTimer = (availableCount: number): CollectableCouponTimerApi => {
  const pathName = usePathname();
  const { data: settings } = useDisplayDurationQuery();

  const [showFloatingButton, setShowFloatingButton] = useState(true);

  const pageSetting = resolvePageSetting(pathName, settings);
  const mode: CollectableCouponDisplayMode | undefined = pageSetting?.Mode ?? undefined;
  const durationSec =
    typeof pageSetting?.DurationSeconds === 'number' && Number.isFinite(pageSetting.DurationSeconds)
      ? pageSetting.DurationSeconds
      : undefined;

  // Reset on route change.
  useEffect(() => {
    setShowFloatingButton(true);
  }, [pathName]);

  useEffect(() => {
    if (availableCount === 0) {
      return;
    }

    if (!pageSetting) {
      return; // wait for API
    }

    if (mode === 'AlwaysDisplay') {
      // No timeout — FAB stays visible indefinitely on this page.
      setShowFloatingButton(true);

      return;
    }

    // TimeBased — hide after the page-specific duration.
    // DurationSeconds <= 0 in TimeBased mode means "hide immediately" (preserves the legacy contract).
    if (durationSec === undefined || durationSec <= 0) {
      setShowFloatingButton(false);

      return;
    }

    const timeoutMs = Math.max(0, Math.floor(durationSec)) * 1000;
    const id = setTimeout(() => setShowFloatingButton(false), timeoutMs);

    return () => clearTimeout(id);
  }, [availableCount, mode, durationSec, pathName, pageSetting]);

  return {
    showFloatingButton,
    hideFloatingButton: () => setShowFloatingButton(false),
    pageSetting,
    settings,
  };
};